import { useState, useEffect } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { GmItemsAwardProps, ItemRecipe, ItemAwardUser, ItemPreview } from '@/types/items';

/**
 * Award type options
 */
type AwardType = 'item' | 'blueprint';

/**
 * GM Items Award page - form to award an item to a user.
 */
export default function Award({ recipes, users, selectedRecipe, stats }: GmItemsAwardProps) {
    const [recipeId, setRecipeId] = useState<number | ''>(selectedRecipe?.id || '');
    const [userId, setUserId] = useState<number | ''>('');
    const [quality, setQuality] = useState<number | ''>('');
    const [awardType, setAwardType] = useState<AwardType>('item');
    const [userSearch, setUserSearch] = useState('');
    const [preview, setPreview] = useState<ItemPreview | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Get the selected recipe
    const recipe = recipes.find(r => r.id === recipeId);

    // Check if blueprint can be awarded for this recipe
    const canAwardBlueprint = recipe?.learnable_by === 'blueprint';

    // Reset award type if blueprint is not available for selected recipe
    useEffect(() => {
        if (!canAwardBlueprint && awardType === 'blueprint') {
            setAwardType('item');
        }
    }, [canAwardBlueprint, awardType]);

    // Load preview when recipe and quality change
    useEffect(() => {
        if (recipeId && quality) {
            setIsLoadingPreview(true);
            fetch('/gm/items/award/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ recipe_id: recipeId, quality }),
            })
                .then(res => res.json())
                .then(data => {
                    setPreview(data);
                    setIsLoadingPreview(false);
                })
                .catch(() => setIsLoadingPreview(false));
        } else {
            setPreview(null);
        }
    }, [recipeId, quality]);

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.post('/gm/items/award', {
            recipe_id: recipeId,
            user_id: userId,
            quality: awardType === 'blueprint' ? null : (quality || null),
            award_type: awardType,
        }, {
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    // Handle user search
    const handleUserSearch = () => {
        router.get('/gm/items/award', {
            recipe_id: recipeId || undefined,
            user_search: userSearch || undefined,
        }, { preserveState: true, only: ['users'] });
    };

    return (
        <GmLayout title="Award Item" activePath="/gm/items">
            <div className="max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Award Item</h1>
                    <Link
                        href="/gm/items"
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ← Back to Recipes
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Recipe Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Item Recipe <span className="text-red-400">*</span>
                        </label>
                        <select
                            value={recipeId}
                            onChange={(e) => setRecipeId(e.target.value ? parseInt(e.target.value) : '')}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            required
                        >
                            <option value="">Select a recipe...</option>
                            {recipes.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name} ({r.rarity.label})
                                </option>
                            ))}
                        </select>
                        {errors.recipe_id && <p className="mt-1 text-sm text-red-400">{errors.recipe_id}</p>}
                        {recipe && (
                            <div className={`mt-2 p-3 bg-slate-800 rounded-lg border border-slate-700`}>
                                <p className={`font-medium ${recipe.rarity.tailwindClass}`}>{recipe.name}</p>
                                {recipe.description && (
                                    <p className="text-sm text-gray-400 mt-1">{recipe.description}</p>
                                )}
                                <p className="text-sm text-gray-500 mt-1">
                                    {recipe.stat_count} stat(s), range {recipe.base_stat_range[0]}-{recipe.base_stat_range[1]}
                                </p>
                                {recipe.learnable_by && recipe.learnable_by !== 'no' && (
                                    <p className="text-sm text-blue-400 mt-1">
                                        📘 Learnable via: {recipe.learnable_by}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Award Type Selection */}
                    {recipe && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Award Type <span className="text-red-400">*</span>
                            </label>
                            <div className="flex gap-4">
                                <label className={`flex items-center gap-2 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                    awardType === 'item'
                                        ? 'bg-green-600/20 border-2 border-green-500'
                                        : 'bg-slate-700 border-2 border-slate-600 hover:border-slate-500'
                                }`}>
                                    <input
                                        type="radio"
                                        name="award_type"
                                        value="item"
                                        checked={awardType === 'item'}
                                        onChange={() => setAwardType('item')}
                                        className="sr-only"
                                    />
                                    <span className="text-xl">🎁</span>
                                    <div>
                                        <p className="font-medium text-white">Crafted Item</p>
                                        <p className="text-xs text-gray-400">Award the actual item with stats</p>
                                    </div>
                                </label>

                                <label className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                                    canAwardBlueprint
                                        ? awardType === 'blueprint'
                                            ? 'bg-blue-600/20 border-2 border-blue-500 cursor-pointer'
                                            : 'bg-slate-700 border-2 border-slate-600 hover:border-slate-500 cursor-pointer'
                                        : 'bg-slate-800 border-2 border-slate-700 opacity-50 cursor-not-allowed'
                                }`}>
                                    <input
                                        type="radio"
                                        name="award_type"
                                        value="blueprint"
                                        checked={awardType === 'blueprint'}
                                        onChange={() => canAwardBlueprint && setAwardType('blueprint')}
                                        disabled={!canAwardBlueprint}
                                        className="sr-only"
                                    />
                                    <span className="text-xl">📘</span>
                                    <div>
                                        <p className="font-medium text-white">Blueprint</p>
                                        <p className="text-xs text-gray-400">
                                            {canAwardBlueprint
                                                ? 'Teach the user to craft this item'
                                                : 'Not learnable via blueprint'}
                                        </p>
                                    </div>
                                </label>
                            </div>
                            {errors.award_type && <p className="mt-1 text-sm text-red-400">{errors.award_type}</p>}
                        </div>
                    )}

                    {/* User Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Recipient <span className="text-red-400">*</span>
                        </label>
                        <div className="flex gap-2 mb-2">
                            <input
                                type="text"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUserSearch())}
                                placeholder="Search users..."
                                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                            <button
                                type="button"
                                onClick={handleUserSearch}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Search
                            </button>
                        </div>
                        <select
                            value={userId}
                            onChange={(e) => setUserId(e.target.value ? parseInt(e.target.value) : '')}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            required
                        >
                            <option value="">Select a user...</option>
                            {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.username} ({u.name})
                                </option>
                            ))}
                        </select>
                        {errors.user_id && <p className="mt-1 text-sm text-red-400">{errors.user_id}</p>}
                    </div>

                    {/* Quality Selection - Only show for item awards */}
                    {awardType === 'item' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Quality <span className="text-gray-500">(optional - random if not set)</span>
                            </label>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((q) => (
                                    <button
                                        key={q}
                                        type="button"
                                        onClick={() => setQuality(quality === q ? '' : q)}
                                        className={`px-4 py-2 rounded transition-colors ${
                                            quality === q
                                                ? 'bg-yellow-600 text-white'
                                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                        }`}
                                    >
                                        {'★'.repeat(q)}
                                    </button>
                                ))}
                            </div>
                            {errors.quality && <p className="mt-1 text-sm text-red-400">{errors.quality}</p>}
                        </div>
                    )}

                    {/* Blueprint Info */}
                    {awardType === 'blueprint' && recipe && (
                        <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <span className="text-2xl">📘</span>
                                <div>
                                    <p className="font-medium text-blue-300">Blueprint: {recipe.name}</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        This will grant the user the ability to craft this item. Blueprints don't have
                                        quality ratings or stats - they teach the recipe itself.
                                    </p>
                                    {recipe.crafting_materials && recipe.crafting_materials.length > 0 && (
                                        <div className="mt-2 text-sm text-gray-400">
                                            <span className="text-gray-500">Crafting requires:</span>{' '}
                                            {recipe.crafting_materials.map((m: { type: string; quantity: number }, i: number) => (
                                                <span key={m.type}>
                                                    {i > 0 && ', '}
                                                    {m.quantity}x {m.type}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview - Only show for item awards */}
                    {awardType === 'item' && recipe && quality && (
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-gray-300 mb-3">Item Preview</h3>
                            {isLoadingPreview ? (
                                <p className="text-gray-400">Loading preview...</p>
                            ) : preview ? (
                                <div className="space-y-2 text-sm">
                                    <p className="text-yellow-400">Quality: {preview.quality_stars}</p>
                                    <p className="text-gray-300">
                                        Stats: {preview.stat_count} stat(s), range {preview.stat_range[0]}-{preview.stat_range[1]}
                                    </p>
                                    {preview.fixed_stats ? (
                                        <div>
                                            <p className="text-gray-400">Fixed Stats:</p>
                                            <ul className="ml-4 text-gray-300">
                                                {Object.entries(preview.fixed_stats).map(([stat, value]) => (
                                                    <li key={stat}>+{value} {stats[stat] || stat}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-gray-400">Possible Stats:</p>
                                            <p className="text-gray-300 ml-4">{preview.possible_stats.join(', ')}</p>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting || !recipeId || !userId}
                            className={`px-6 py-2 text-white rounded transition-colors disabled:opacity-50 ${
                                awardType === 'blueprint'
                                    ? 'bg-blue-600 hover:bg-blue-700'
                                    : 'bg-green-600 hover:bg-green-700'
                            }`}
                        >
                            {isSubmitting
                                ? 'Awarding...'
                                : awardType === 'blueprint'
                                    ? '📘 Award Blueprint'
                                    : '🎁 Award Item'}
                        </button>
                        <Link
                            href="/gm/items"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}
